import { ArrowUpRight, BookOpen, Copy, Globe, Info, Loader2, Plus, Rocket, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Github } from "@/components/icons/github";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  cancelProjectScaffold,
  createProject,
  getProject,
  isApiError,
  useGithubStatus,
  useGroups,
  useMyGroups,
  useVersion,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { shortcut } from "@/lib/platform";
import { DEPLOY_URL_WINDOW_MS } from "@/lib/project-status";
import { useUser } from "@/lib/user-context";

import { InstructionsDialog } from "./instructions-dialog";
import { type CompletionData, ScaffoldProgress } from "./scaffold-progress";

type Step = "review" | "progress" | "complete";

interface NewProjectDialogProps {
  trigger?: React.ReactNode;
  onProjectCreated: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/;

// Sentinel Select value for "no group" (leaves the project ungrouped). Cannot
// collide with a real group id (ids are numeric).
const NO_GROUP = "__none__";

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

// Public hostname label (edge-enabled instances only). Mirrors the backend's
// rule; the backend also refuses reserved and already-taken names.
const DNS_LABEL_RE = /^[a-z0-9]([a-z0-9-]{0,22}[a-z0-9])?$/;

// The label a project gets when none is typed: the backend's Azure slug
// (kebab-case, "a" prefix before a leading digit, 16 characters).
function defaultDnsLabel(name: string): string {
  const kebab = toSlug(name);
  const prefixed = /^[0-9]/.test(kebab) ? `a${kebab}` : kebab;
  return prefixed.slice(0, 16).replace(/-+$/, "");
}

function dnsLabelError(label: string): string | null {
  if (!DNS_LABEL_RE.test(label)) {
    return "Use 1-24 lowercase letters, digits and hyphens; no hyphen at the start or end.";
  }
  if (label.endsWith("-dev") || label.endsWith("-test")) {
    return 'The address cannot end in "-dev" or "-test": those endings name the environments.';
  }
  return null;
}

function isValidSlug(slug: string): boolean {
  return SLUG_RE.test(slug);
}

function getApiErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    const detail = (err.body as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string") {
      return detail;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: unknown };
      if (typeof first?.msg === "string") {
        return first.msg;
      }
    }
  }
  return "Failed to create project. Please try again.";
}

function isValidDomain(domain: string): boolean {
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(
    domain.toLowerCase(),
  );
}

export function NewProjectDialog({
  trigger,
  onProjectCreated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: NewProjectDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (v: boolean) => controlledOnOpenChange?.(v) : setInternalOpen;
  const [step, setStep] = useState<Step>("review");

  // Shared with the GitHub connect banner / org-access warning -- same query
  // key, so this reuses the already-cached status rather than refetching.
  const { data: githubStatusData } = useGithubStatus();
  const githubOrg = githubStatusData?.github_org;

  // Set only on an instance that gives new projects a public hostname; the
  // web address field below is shown only then.
  const { data: versionData } = useVersion();
  const edgeBaseDomain = versionData?.edge_base_domain ?? null;
  // The always-allowed sign-in domains, shown as non-removable chips. Served
  // by the backend (`GET /version`) -- the same list an MCP-created project
  // gets -- so there is one copy of it.
  const defaultDomains = useMemo(
    () => versionData?.new_project_email_domains ?? [],
    [versionData?.new_project_email_domains],
  );

  // Groups the current user can assign the project to (NOR-305). Admins may
  // assign any group; non-admins only ones they belong to -- the backend
  // enforces this too.
  const { user: viewer } = useUser();
  const isAdmin = viewer?.role === "admin";
  const { data: myGroups = [] } = useMyGroups();
  const { data: allGroups = [] } = useGroups(isAdmin);
  const assignableGroups = isAdmin ? allGroups : myGroups;

  // Step 1: Review
  const [projectName, setProjectName] = useState("");
  const [clientSlug, setClientSlug] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [demoEmail, setDemoEmail] = useState("");
  // Domains the user added on top of the defaults.
  const [extraDomains, setExtraDomains] = useState<string[]>([]);
  const allowedDomains = useMemo(
    () => [...defaultDomains, ...extraDomains.filter((d) => !defaultDomains.includes(d))],
    [defaultDomains, extraDomains],
  );
  const [newDomain, setNewDomain] = useState("");
  const [groupId, setGroupId] = useState(NO_GROUP);
  const [aiEnabled, setAiEnabled] = useState(false);
  // `null` until the user edits the field: the label then follows the name.
  const [dnsLabelInput, setDnsLabelInput] = useState<string | null>(null);

  const demoEmailTouched = useRef(false);

  // Step 2: Progress
  const [projectId, setProjectId] = useState<number | null>(null);

  const [cancelling, setCancelling] = useState(false);

  // Step 3: Complete
  const [completionData, setCompletionData] = useState<CompletionData | null>(null);
  // The deployed app URL arrives minutes after scaffold completion, via the
  // deploy callback (NOR-292) -- polled for on the completion screen below.
  const [appUrl, setAppUrl] = useState<string | null>(null);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const [error, setError] = useState("");

  const projectSlug = toSlug(projectName);
  const rawRepoSlug = clientSlug && projectSlug ? `${clientSlug}-${projectSlug}` : "";
  // Truncate to 63 chars (backend max) and strip trailing hyphens from truncation
  const repoSlug = rawRepoSlug.slice(0, 63).replace(/-+$/, "");
  const repoSlugValid = repoSlug.length >= 2 && isValidSlug(repoSlug);

  const dnsLabel = dnsLabelInput ?? defaultDnsLabel(projectName);
  const dnsLabelProblem = edgeBaseDomain && dnsLabel ? dnsLabelError(dnsLabel) : null;
  const dnsLabelValid = !edgeBaseDomain || (!!dnsLabel && !dnsLabelProblem);

  // Copy-pastable prompt for the completion screen: the champion drops this
  // into Claude (Desktop) to clone the new repo and run first-time setup.
  const setupPrompt = completionData?.githubUrl
    ? `Clone my new project from ${completionData.githubUrl}, then set it up for me: run \`make prepare\` to install everything and \`make dev\` to start it, and tell me the URL to open in my browser.`
    : "";

  function copySetupPrompt() {
    if (!setupPrompt) {
      return;
    }
    navigator.clipboard.writeText(setupPrompt);
    toast.success("Prompt copied — paste it into Claude to set up your project");
  }

  function resetState() {
    setStep("review");
    setProjectName("");
    setClientSlug("");
    setDescription("");
    setProjectId(null);
    setCompletionData(null);
    setAppUrl(null);
    setError("");
    setCreating(false);
    setCancelling(false);
    setDemoEmail("");
    setExtraDomains([]);
    setNewDomain("");
    setGroupId(NO_GROUP);
    setAiEnabled(false);
    setDnsLabelInput(null);
    demoEmailTouched.current = false;
  }

  async function handleCreate() {
    if (!projectName.trim() || !repoSlug || !clientSlug.trim() || !description.trim()) {
      setError("Please fill in all required fields.");
      return;
    }
    setCreating(true);
    setError("");

    try {
      const { data: project } = await createProject({
        body: {
          project_name: projectName.trim(),
          client_slug: clientSlug.trim(),
          project_slug: repoSlug,
          description: description.trim(),
          python_version: "3.13",
          node_version: "22",
          demo_email: demoEmail.trim(),
          allowed_email_domains: allowedDomains,
          group_id: groupId === NO_GROUP ? null : Number(groupId),
          ai_enabled: aiEnabled,
          dns_label: edgeBaseDomain ? dnsLabel : null,
        },
      });
      if (!project) {
        throw new Error("empty create response");
      }
      setProjectId(project.id);
      setStep("progress");
    } catch (err) {
      logger.error("[new-project] creation failed:", err);
      setError(getApiErrorMessage(err));
      setCreating(false);
    }
  }

  function handleAddDomain() {
    const domain = newDomain.trim().toLowerCase();
    if (!domain || !isValidDomain(domain) || allowedDomains.includes(domain)) {
      return;
    }
    setExtraDomains((prev) => [...prev, domain]);
    setNewDomain("");
  }

  function handleRemoveDomain(domain: string) {
    if (defaultDomains.includes(domain)) {
      return;
    }
    setExtraDomains((prev) => prev.filter((d) => d !== domain));
  }

  const clientSlugValid = isValidSlug(clientSlug);
  const demoEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(demoEmail);
  const canCreate =
    !creating &&
    // The default domains come from the backend; wait for them.
    defaultDomains.length > 0 &&
    !!projectName.trim() &&
    clientSlugValid &&
    repoSlugValid &&
    dnsLabelValid &&
    !!description.trim() &&
    demoEmailValid;

  const handleCreateRef = useRef(handleCreate);
  // Keep the ref pointing at the latest handler — written in an effect so the
  // react-hooks/refs rule (no ref writes during render) stays happy.
  useEffect(() => {
    handleCreateRef.current = handleCreate;
  });

  useEffect(() => {
    if (step !== "review") {
      return;
    }
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canCreate) {
        e.preventDefault();
        handleCreateRef.current();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [step, canCreate]);

  // After completion, the deploy (GitHub Actions) keeps running and reports the
  // live app URL back minutes later (NOR-292). Poll for it so the "Open app"
  // button can appear on this screen, not only on the project card once the
  // dialog is closed.
  useEffect(() => {
    if (step !== "complete" || !projectId || appUrl) {
      return;
    }
    const pid = projectId;
    let cancelled = false;
    const INTERVAL_MS = 10_000;

    async function pollAppUrl() {
      const start = Date.now();
      while (!cancelled && Date.now() - start < DEPLOY_URL_WINDOW_MS) {
        try {
          const { data } = await getProject({ path: { project_id: pid } });
          // A project with its own web address is only "open-able" once that
          // address is live, which can be well after the first deploy.
          const live = !data?.edge_hostname || data.edge_status === "serving";
          if (data?.app_url && live && !cancelled) {
            setAppUrl(data.app_url);
            return;
          }
        } catch (err) {
          logger.debug("[new-project] app-url poll — not ready yet", err);
        }
        if (!cancelled) {
          await new Promise((r) => setTimeout(r, INTERVAL_MS));
        }
      }
    }

    pollAppUrl();
    return () => {
      cancelled = true;
    };
  }, [step, projectId, appUrl]);

  useEffect(() => {
    if (step !== "review" || demoEmailTouched.current) {
      return;
    }
    if (clientSlug) {
      setDemoEmail(`${clientSlug}@boardofinnovation.com`);
    }
  }, [step, clientSlug]);

  function handleComplete(data: CompletionData) {
    setCompletionData(data);
    setStep("complete");
  }

  function handleDone() {
    setOpen(false);
    resetState();
    onProjectCreated();
  }

  async function handleCancel() {
    if (!projectId) {
      return;
    }
    setCancelling(true);
    setError("");
    try {
      await cancelProjectScaffold({ path: { project_id: projectId } });
      handleDone();
    } catch (err) {
      logger.error("[new-project] cancel failed:", err);
      setError("Could not cancel scaffold. Please try again.");
      setCancelling(false);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v && step === "progress") {
            return;
          }
          if (!v && step === "complete") {
            handleDone();
            return;
          }
          setOpen(v);
          if (!v) {
            resetState();
          }
        }}
      >
        {trigger && !isControlled && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          className="sm:max-w-4xl"
          showCloseButton={step !== "progress"}
          onInteractOutside={(e) => {
            if (step === "progress") {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (step === "progress") {
              e.preventDefault();
            }
          }}
        >
          {step === "review" && (
            <>
              <DialogHeader>
                <DialogTitle>Review &amp; confirm</DialogTitle>
                <DialogDescription>
                  Check the details below and create your project.
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
                      Project Details
                    </h3>
                    <Separator className="mb-3" />
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="project-name">Project Name</Label>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>A human-readable name for the project.</p>
                            <p className="text-background/60 mt-1">
                              Letters, digits, spaces, hyphens, ampersands, parentheses, and dots.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="project-name"
                      value={projectName}
                      onChange={(e) =>
                        setProjectName(e.target.value.replace(/[^a-zA-Z0-9 \-&().]/g, ""))
                      }
                      placeholder="e.g. Customer Feedback Dashboard"
                      maxLength={100}
                    />
                    <p className="text-muted-foreground -mt-0.5 text-right text-xs">
                      {projectName.length}/100
                    </p>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="client-slug">Team Identifier</Label>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>
                              Short kebab-case identifier for the owning team, used in the repo
                              name.
                            </p>
                            <p className="text-background/60 mt-1">
                              e.g. finance, operations, supply-chain
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="client-slug"
                      value={clientSlug}
                      onChange={(e) =>
                        setClientSlug(
                          e.target.value
                            .toLowerCase()
                            .replace(/\s+/g, "-")
                            .replace(/[^a-z0-9-]/g, "")
                            .replace(/-+/g, "-"),
                        )
                      }
                      placeholder="e.g. finance"
                      maxLength={50}
                    />
                    <p className="text-muted-foreground -mt-0.5 text-right text-xs">
                      {clientSlug.length}/50
                    </p>
                    {clientSlug && !clientSlugValid && (
                      <p className="text-destructive text-xs">
                        Team identifier must be 2-63 characters, lowercase alphanumeric and hyphens
                        only, and cannot start or end with a hyphen.
                      </p>
                    )}
                  </div>

                  {repoSlug && (
                    <div>
                      <div
                        className={`flex items-center gap-2 rounded-md border px-3 py-2 ${repoSlug && !repoSlugValid ? "border-destructive" : ""}`}
                      >
                        <Github className="text-muted-foreground size-4 shrink-0" />
                        <span className="text-sm">
                          {githubOrg && <span className="text-muted-foreground">{githubOrg}/</span>}
                          <span className="font-semibold">{repoSlug}</span>
                        </span>
                      </div>
                      {repoSlug && !repoSlugValid && (
                        <p className="text-destructive mt-1 text-xs">
                          Repository slug must be 2-63 characters, lowercase alphanumeric and
                          hyphens only, and cannot start or end with a hyphen.
                        </p>
                      )}
                    </div>
                  )}

                  {edgeBaseDomain && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="dns-label">Web address</Label>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="text-muted-foreground size-3.5 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              <p>The name your app is reachable under, one per environment.</p>
                              <p className="text-background/60 mt-1">
                                It cannot be changed after the project is created.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Input
                        id="dns-label"
                        value={dnsLabel}
                        onChange={(e) =>
                          setDnsLabelInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))
                        }
                        placeholder="e.g. feedback"
                        maxLength={24}
                        aria-invalid={!!dnsLabelProblem}
                      />
                      {dnsLabelProblem && (
                        <p className="text-destructive text-xs">{dnsLabelProblem}</p>
                      )}
                      {dnsLabel && !dnsLabelProblem && (
                        <div className="flex items-start gap-2 rounded-md border px-3 py-2">
                          <Globe className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                          <div className="space-y-0.5 text-sm">
                            {[
                              ["dev", `${dnsLabel}-dev`],
                              ["test", `${dnsLabel}-test`],
                              ["prod", dnsLabel],
                            ].map(([environment, host]) => (
                              <p key={environment}>
                                <span className="text-muted-foreground inline-block w-10">
                                  {environment}
                                </span>
                                <span className="font-semibold">{host}</span>
                                <span className="text-muted-foreground">.{edgeBaseDomain}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="description">Description</Label>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>A short summary visible on GitHub and in the project list.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="A concise description of the project"
                      rows={3}
                      maxLength={500}
                    />
                    <p className="text-muted-foreground -mt-0.5 text-right text-xs">
                      {description.length}/500
                    </p>
                  </div>

                  <div>
                    <h3 className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
                      Configuration
                    </h3>
                    <Separator className="mb-3" />
                  </div>

                  {assignableGroups.length > 0 && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="project-group">Team / Group</Label>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="text-muted-foreground size-3.5 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              <p>Scopes who can see and be assigned to this project.</p>
                              <p className="text-background/60 mt-1">
                                Leave as &ldquo;No group&rdquo; to keep it visible only to you and
                                anyone you add as a member.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Select value={groupId} onValueChange={setGroupId}>
                        <SelectTrigger id="project-group" className="w-full">
                          <SelectValue placeholder="No group" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_GROUP}>No group</SelectItem>
                          {assignableGroups.map((group) => (
                            <SelectItem key={group.id} value={String(group.id)}>
                              {group.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Deliberately the loudest control in the form: forgetting it means a
                      plain project with no Foundry / Langfuse / Serper wiring, and the flag
                      can't be flipped afterwards without re-creating the project. */}
                  <div
                    className={
                      aiEnabled
                        ? "border-primary/60 bg-primary/5 rounded-lg border p-4"
                        : "border-border rounded-lg border p-4"
                    }
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="space-y-1">
                        <Label htmlFor="ai-enabled" className="text-base font-semibold">
                          AI-enabled
                        </Label>
                        <p id="ai-enabled-description" className="text-muted-foreground text-sm">
                          Wires Azure AI Foundry, Langfuse tracing, Serper web search and pgvector
                          RAG into the project. Leave off for a plain project. Cannot be changed
                          after creation.
                        </p>
                      </div>
                      <Switch
                        id="ai-enabled"
                        size="lg"
                        aria-describedby="ai-enabled-description"
                        checked={aiEnabled}
                        onCheckedChange={setAiEnabled}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="demo-email">Demo Email</Label>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>Initial admin user for the scaffolded app.</p>
                            <p className="text-background/60 mt-1">
                              The email can be fictive — no verification emails will be sent.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="demo-email"
                      type="email"
                      value={demoEmail}
                      onChange={(e) => {
                        setDemoEmail(e.target.value);
                        demoEmailTouched.current = true;
                      }}
                      placeholder="e.g. demo@boardofinnovation.com"
                      maxLength={254}
                    />
                    {demoEmail && !demoEmailValid && (
                      <p className="text-destructive text-xs">
                        Please enter a valid email address.
                      </p>
                    )}
                  </div>

                  <div className="space-y-3 rounded-lg border p-3">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-sm font-medium">Allowed Email Domains</Label>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>Email domains allowed to sign up in the scaffolded app.</p>
                            <p className="text-background/60 mt-1">
                              {defaultDomains.join(" and ")} are always included.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {allowedDomains.map((domain) => (
                        <span
                          key={domain}
                          className="bg-secondary text-secondary-foreground inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm"
                        >
                          {domain}
                          {!defaultDomains.includes(domain) && (
                            <button
                              type="button"
                              onClick={() => handleRemoveDomain(domain)}
                              className="text-muted-foreground hover:text-foreground ml-0.5"
                            >
                              <X className="size-3" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="e.g. acme.com"
                        value={newDomain}
                        onChange={(e) =>
                          setNewDomain(e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ""))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddDomain();
                          }
                        }}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddDomain}
                        disabled={!newDomain.trim() || !isValidDomain(newDomain.trim())}
                      >
                        <Plus className="size-4" />
                      </Button>
                    </div>
                  </div>

                  {error && <p className="text-destructive text-sm">{error}</p>}

                  <div className="flex justify-end">
                    <Button onClick={handleCreate} disabled={!canCreate}>
                      {creating && <Loader2 className="mr-2 size-4 animate-spin" />}
                      {creating ? "Creating..." : "Create Project"}
                      {!creating && (
                        <span className="text-muted-foreground ml-1 hidden text-xs sm:inline">
                          {shortcut("Enter")}
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogBody>
            </>
          )}

          {step === "progress" && projectId !== null && (
            <>
              <DialogHeader>
                <DialogTitle>Creating your project</DialogTitle>
                <DialogDescription>Sit tight while we set everything up.</DialogDescription>
              </DialogHeader>
              <DialogBody>
                <ScaffoldProgress
                  projectId={projectId}
                  onComplete={handleComplete}
                  onFailed={handleDone}
                />
                {error && <p className="text-destructive mt-2 text-sm">{error}</p>}
                <div className="mt-4 flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="text-destructive hover:!bg-destructive/10 hover:!text-destructive"
                  >
                    {cancelling ? "Cancelling..." : "Cancel scaffold"}
                  </Button>
                </div>
              </DialogBody>
            </>
          )}

          {step === "complete" && completionData && (
            <DialogBody className="py-4 text-center">
              <h2 className="mb-1 text-2xl font-normal tracking-tight">Happy vibe coding!</h2>
              <p className="text-muted-foreground mb-6 text-sm">
                Your project is live and ready to go. Now go build something amazing.
              </p>

              {completionData.githubUrl && (
                <div className="mb-5 space-y-2 rounded-lg border p-3 text-left">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">Set up in Claude</p>
                    <Button size="sm" variant="outline" onClick={copySetupPrompt}>
                      <Copy className="mr-2 size-3.5" />
                      Copy prompt
                    </Button>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    Paste this into Claude to clone and start your project.
                  </p>
                  <pre className="bg-muted/40 overflow-x-auto rounded border p-2 font-mono text-xs whitespace-pre-wrap">
                    {setupPrompt}
                  </pre>
                </div>
              )}

              {/* Deployed app URL: arrives minutes after scaffolding via the
                  deploy callback, so poll and show a waiting state until then. */}
              {appUrl ? (
                <a href={appUrl} target="_blank" rel="noopener noreferrer" className="mb-3 block">
                  <Button className="w-full gap-2">
                    <Rocket className="size-4" /> Open app <ArrowUpRight className="size-4" />
                  </Button>
                </a>
              ) : (
                <div className="text-muted-foreground mb-3 flex items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-3 text-sm">
                  <Loader2 className="size-4 animate-spin" />
                  Deploying your app… the link appears here and on your project card in a few
                  minutes.
                </div>
              )}

              {projectId && (
                <Button
                  variant="outline"
                  className="mb-5 w-full gap-2"
                  onClick={() => setInstructionsOpen(true)}
                >
                  <BookOpen className="size-4" /> How to get started
                </Button>
              )}

              <div className="flex flex-wrap justify-center gap-2">
                {completionData.githubUrl && (
                  <a href={completionData.githubUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="gap-1.5">
                      <Github className="size-4" /> GitHub
                    </Button>
                  </a>
                )}
              </div>

              {completionData.hasWarnings && completionData.message && (
                <p className="mt-4 text-xs text-amber-600 dark:text-amber-400">
                  {completionData.message}
                </p>
              )}

              <div className="mt-6">
                <Button variant="ghost" size="sm" onClick={handleDone}>
                  Close
                </Button>
              </div>
            </DialogBody>
          )}
        </DialogContent>
      </Dialog>

      {projectId && (
        <InstructionsDialog
          projectId={projectId}
          open={instructionsOpen}
          onOpenChange={setInstructionsOpen}
        />
      )}
    </>
  );
}
